import React, { useEffect, useRef } from 'react';
import Chart, { ChartConfiguration } from 'chart.js/auto';

export type AppStackedBarChartProps = {
    name: string;
    data: {
        labels: string[];
        dataSet: {
            id: string | number;
            label: string;
            data: (string | number)[];
            color?: string;
        }[];
        totals?: (string | number)[];
    };
    style?: React.CSSProperties;
    legend?: boolean;
}

const AppStackedBarChart = (props: AppStackedBarChartProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const chartRef = useRef<Chart | null>(null);

    useEffect(() => {
        if (!canvasRef.current) return;

        if (chartRef.current) {
            chartRef.current.destroy();
        }

        const datasets = props.data.dataSet.map((ds, i) => ({
            ...ds,
            backgroundColor: ds.color,
        }));

        const config: ChartConfiguration = {
            type: 'bar',
            data: {
                datasets: datasets as any,
                labels: props.data.labels
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: props.legend || false,
                        position: 'bottom',
                    }
                },
                scales: {
                    y: {
                        stacked: true,
                        beginAtZero: true
                    },
                    x: {
                        stacked: true
                    }
                }
            },
            plugins: [{
                id: 'totalLabels',
                afterDatasetsDraw(chart) {
                    const { ctx, data } = chart;
                    ctx.save();
                    ctx.font = 'bold 12px sans-serif';
                    ctx.fillStyle = '#666';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'bottom';

                    if (!props.data.totals?.length) {
                        return;
                    }

                    const totals = props.data.totals;
                    for (let index = 0; index < totals.length; index++) {
                        let topY = chart.chartArea ? chart.chartArea.bottom : 0;
                        let barX = 0;
                        let hasVisibleBar = false;

                        data.datasets.forEach((_, datasetIndex) => {
                            if (!chart.isDatasetVisible(datasetIndex)) return;
                            const meta = chart.getDatasetMeta(datasetIndex);
                            const bar = meta.data[index] as any;
                            if (bar && !isNaN(bar.x) && !isNaN(bar.y)) {
                                hasVisibleBar = true;
                                barX = bar.x;
                                if (bar.y < topY) {
                                    topY = bar.y;
                                }
                            }
                        });

                        if (hasVisibleBar) {
                            const totalText = Number.isInteger(totals[index]) ? Number(totals[index]) : totals[index];
                            ctx.fillText(totalText.toString(), barX, topY - 5);
                        }
                    }

                    ctx.restore();
                }
            }]
        };

        chartRef.current = new Chart(canvasRef.current, config);

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [props.data, props.legend]);

    return (
        <div style={{ position: 'relative', height: '100%', width: '100%', minHeight: '200px', ...props.style }}>
            <canvas ref={canvasRef} />
        </div>
    );
};

export default AppStackedBarChart;